import { FormEvent, useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useEvent, useEventOccurrences } from '../hooks/usePotlucks'
import { useUpdateEvent, useDeleteEvent } from '../hooks/useChurchAdmin'
import { useChurch } from '../hooks/useChurches'
import { geocodeAddress } from '../lib/geocode'
import { ApiError } from '../lib/api'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EventBadge } from '../components/ui/EventBadge'
import { Icons } from '../components/ui/Icons'
import LocationPicker from '../components/LocationPicker'
import { ImageUpload } from '../components/admin/ImageUpload'
import type { EventType, EventAttributes } from '@fellowship/shared'
import { ATTRIBUTE_OPTIONS } from './CreatePersonEventPage'
import { EventTimePicker } from '../components/ui/DateTimePicker'
import { CapabilityToggleRow } from '../components/event/CapabilityToggleRow'
import { DishGoalsFields } from '../components/event/DishGoalsFields'
import { OutdoorDetailsFields } from '../components/event/OutdoorDetailsFields'

function Field({
  label, htmlFor, hint, error, required, children,
}: {
  label: string; htmlFor?: string; hint?: string; error?: string; required?: boolean; children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-ink-2 flex gap-1">
        {label}{required && <span className="text-warn">*</span>}
      </label>
      {hint && <div className="text-[11.5px] text-muted -mt-1">{hint}</div>}
      {children}
      {error && <div className="text-[12px] text-[#A33A2A]">{error}</div>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-line-soft rounded-2xl px-4 sm:px-6 py-[22px]">
      <div className="text-[13px] font-bold text-ink-2 tracking-[0.04em] uppercase mb-[18px]">
        {title}
      </div>
      {children}
    </div>
  )
}


function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export default function EditPersonEventPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: eventData, isLoading } = useEvent(id)
  const { data: occurrencesData, isLoading: occurrencesLoading } = useEventOccurrences(id)
  const updateEvent = useUpdateEvent(id!)

  const event = eventData?.data
  const eventType = event?.eventType ?? 'potluck'
  const isChurchHosted = event?.hostedByType === 'church'
  const { data: churchData } = useChurch(isChurchHosted ? event?.hostedById : undefined)
  const deleteEvent = useDeleteEvent()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [locationName, setLocationName] = useState('')
  const [addressText, setAddressText] = useState('')
  const [locationLat, setLocationLat] = useState<number | null>(null)
  const [locationLng, setLocationLng] = useState<number | null>(null)
  const [locationMode, setLocationMode] = useState<'private' | 'venue' | 'pin'>('private')
  const [audience, setAudience] = useState<'everyone' | 'denomination'>('everyone')
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'invite_only'>('public')
  const [attributes, setAttributes] = useState<EventAttributes>({})
  const [rsvpRequiresApproval, setRsvpRequiresApproval] = useState(true)
  const [hideAddressUntilApproved, setHideAddressUntilApproved] = useState(true)
  const [maxAttendees, setMaxAttendees] = useState('')

  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')

  // Dish coordination
  const [mainGoal, setMainGoal] = useState(0)
  const [saladGoal, setSaladGoal] = useState(0)
  const [sideGoal, setSideGoal] = useState(0)
  const [dessertGoal, setDessertGoal] = useState(0)
  const [drinkGoal, setDrinkGoal] = useState(0)

  // Outdoor activity
  const [outdoorDifficulty, setOutdoorDifficulty] = useState<'easy' | 'moderate' | 'strenuous'>('easy')
  const [outdoorDistance, setOutdoorDistance] = useState('')
  const [outdoorElevation, setOutdoorElevation] = useState('')
  const [outdoorMeetupNote, setOutdoorMeetupNote] = useState('')

  // Study
  const [studyTopic, setStudyTopic] = useState('')
  const [studyScripture, setStudyScripture] = useState('')

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [geocodeError, setGeocodeError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Pre-populate form once event and first occurrence load
  const firstOccurrence = occurrencesData?.data?.[0]
  useEffect(() => {
    if (!event || !firstOccurrence || initialized) return
    setTitle(event.title)
    setDescription(event.description ?? '')
    setLocationName(event.locationName ?? '')
    setAddressText(event.addressText)
    setLocationLat(event.locationLat)
    setLocationLng(event.locationLng)
    if (event.locationName) {
      setLocationMode('venue')
    } else if (!event.addressText && event.locationLat && event.locationLng) {
      setLocationMode('pin')
    } else {
      setLocationMode('private')
    }
    setAudience(event.audience ?? 'everyone')
    setVisibility(event.visibility)
    setRsvpRequiresApproval(event.rsvpRequiresApproval)
    setHideAddressUntilApproved(event.hideAddressUntilApproved)
    setMaxAttendees(event.maxAttendees ? String(event.maxAttendees) : '')
    setImageUrl(event.imageUrl ?? null)
    setStartsAt(isoToLocalDatetime(firstOccurrence.startsAt))
    if (firstOccurrence.endsAt) setEndsAt(isoToLocalDatetime(firstOccurrence.endsAt))

    const meta = event.metadata as Record<string, unknown> | null ?? {}
    const attrs = { ...(event.attributes ?? {}) }

    // Infer capability flags from existing metadata for backward compat
    if (meta.dishGoals) attrs.bringADish = true
    if (meta.difficulty != null || meta.distanceMiles != null) attrs.outdoorActivity = true

    setAttributes(attrs)

    if (attrs.bringADish) {
      const goals = (meta.dishGoals as Record<string, number> | undefined) ?? {}
      setMainGoal(goals.main ?? 4)
      setSaladGoal(goals.salad ?? 3)
      setSideGoal(goals.side ?? 2)
      setDessertGoal(goals.dessert ?? 2)
      setDrinkGoal(goals.drink ?? 2)
    }
    if (attrs.outdoorActivity) {
      setOutdoorDifficulty((meta.difficulty as 'easy' | 'moderate' | 'strenuous') ?? 'easy')
      setOutdoorDistance(meta.distanceMiles != null ? String(meta.distanceMiles) : '')
      setOutdoorElevation(meta.elevationFt != null ? String(meta.elevationFt) : '')
      setOutdoorMeetupNote((meta.meetupNote as string) ?? '')
    }
    if (event.eventType === 'study') {
      setStudyTopic((meta.topic as string) ?? '')
      setStudyScripture((meta.scriptureRef as string) ?? '')
    }
    setInitialized(true)
  }, [event, firstOccurrence, initialized])

  function validate() {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Title is required'
    if (!startsAt) errs.startsAt = 'Start date and time is required'
    if (!addressText.trim()) errs.addressText = 'Address is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function buildMetadata(): Record<string, unknown> | undefined {
    const meta: Record<string, unknown> = {}
    if (attributes.bringADish) {
      const dishGoals: Record<string, number> = {}
      if (mainGoal > 0) dishGoals.main = mainGoal
      if (saladGoal > 0) dishGoals.salad = saladGoal
      if (sideGoal > 0) dishGoals.side = sideGoal
      if (dessertGoal > 0) dishGoals.dessert = dessertGoal
      if (drinkGoal > 0) dishGoals.drink = drinkGoal
      if (Object.keys(dishGoals).length > 0) meta.dishGoals = dishGoals
    }
    if (attributes.outdoorActivity) {
      meta.difficulty = outdoorDifficulty
      if (outdoorDistance) meta.distanceMiles = parseFloat(outdoorDistance)
      if (outdoorElevation) meta.elevationFt = parseInt(outdoorElevation)
      if (outdoorMeetupNote.trim()) meta.meetupNote = outdoorMeetupNote.trim()
    }
    if (eventType === 'study') {
      if (studyTopic.trim()) meta.topic = studyTopic.trim()
      if (studyScripture.trim()) meta.scriptureRef = studyScripture.trim()
    }
    return Object.keys(meta).length > 0 ? meta : undefined
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return

    setGeocodeError('')
    setIsSubmitting(true)

    const coords = locationLat !== null && locationLng !== null
      ? { lat: locationLat, lng: locationLng }
      : await geocodeAddress(addressText.trim())

    if (!coords) {
      setGeocodeError('Could not locate that address. Use the "Locate →" button or drag the map pin.')
      setIsSubmitting(false)
      return
    }

    updateEvent.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        locationName: locationName.trim() || undefined,
        addressText: addressText.trim(),
        locationLat: coords.lat,
        locationLng: coords.lng,
        visibility,
        audience: isChurchHosted ? audience : 'everyone',
        imageUrl,
        attributes,
        rsvpRequiresApproval,
        hideAddressUntilApproved: rsvpRequiresApproval && hideAddressUntilApproved,
        maxAttendees: maxAttendees ? parseInt(maxAttendees) : undefined,
        metadata: buildMetadata(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
      },
      {
        onSuccess: () => navigate(`/events/${id}`),
        onSettled: () => setIsSubmitting(false),
      },
    )
  }

  const serverError = updateEvent.error instanceof ApiError ? updateEvent.error.message : null

  const VISIBILITY_OPTIONS = [
    { value: 'public' as const, label: 'Public', description: 'Appears in search and map for everyone', Icon: Icons.globe },
    { value: 'unlisted' as const, label: 'Unlisted', description: 'Only accessible via direct link', Icon: Icons.link },
    { value: 'invite_only' as const, label: 'Invite only', description: 'Only people you invite can RSVP', Icon: Icons.lock },
  ]

  if (isLoading || occurrencesLoading || !event || !initialized) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 pt-10 pb-20 overflow-x-hidden">
        <Link
          to={`/events/${id}`}
          className="text-[13px] text-muted inline-flex items-center gap-1.5 no-underline mb-5 font-medium"
        >
          <Icons.back size={13} /> Back to event
        </Link>

        <div className="flex items-center gap-3 mb-7">
          <EventBadge type={eventType as EventType} size="md" />
          <h1 className="serif m-0 text-[36px] tracking-[-0.02em]">Edit event</h1>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

          {(serverError || geocodeError) && (
            <div
              role="alert"
              className="px-4 py-3 rounded-xl text-[13px] bg-danger-soft border border-danger-line text-danger-deep"
            >
              {serverError ?? geocodeError}
            </div>
          )}

          {/* Details */}
          <Section title="Details">
            <div className="flex flex-col gap-4">
              <Field label="Title" htmlFor="title" required error={errors.title}>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  aria-invalid={Boolean(errors.title)}
                  className="input"
                />
              </Field>
              <Field label="Description" htmlFor="description">
                <textarea
                  id="description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input h-auto py-3 resize-y leading-relaxed"
                />
              </Field>
            </div>
          </Section>

          {/* When */}
          <Section title="When">
            <EventTimePicker
              startsAt={startsAt}
              endsAt={endsAt}
              onStartsAtChange={setStartsAt}
              onEndsAtChange={setEndsAt}
              startsAtError={errors.startsAt}
            />
          </Section>

          {/* Where */}
          <Section title="Where">
            <LocationPicker
              addressText={addressText}
              onAddressChange={setAddressText}
              lat={locationLat}
              lng={locationLng}
              onCoordinatesChange={(lat, lng) => { setLocationLat(lat); setLocationLng(lng) }}
              addressError={errors.addressText}
              showModeToggle
              mode={locationMode}
              onModeChange={setLocationMode}
              onLocationNameChange={setLocationName}
            />
          </Section>

          {/* Visibility */}
          <Section title="Visibility">
            <div className="flex flex-col gap-2">
              {VISIBILITY_OPTIONS.map(({ value: v, label, description, Icon }) => {
                const selected = visibility === v
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVisibility(v)}
                    aria-pressed={selected}
                    aria-label={label}
                    className={`flex items-center gap-3 px-3.5 py-3 rounded-xl cursor-pointer text-left font-sans border-[1.5px] ${selected ? 'bg-primary-soft border-primary' : 'bg-s2 border-line-soft'}`}
                  >
                    <div
                      className={`w-[34px] h-[34px] rounded-[9px] shrink-0 flex items-center justify-center ${selected ? 'bg-primary text-on-primary' : 'bg-line-soft text-muted'}`}
                    >
                      <Icon size={15} />
                    </div>
                    <div className="flex-1">
                      <div className="text-[13.5px] font-semibold text-ink mb-px">{label}</div>
                      <div className="text-[12px] text-muted">{description}</div>
                    </div>
                    {selected && <Icons.check size={14} className="text-primary shrink-0" />}
                  </button>
                )
              })}
              {visibility === 'invite_only' && (
                <div className="text-[12px] text-muted pl-0.5 mt-0.5">
                  You can invite people from the event's manage page.
                </div>
              )}
            </div>
          </Section>

          {/* Photo */}
          <Section title="Photo">
            {imageUrl ? (
              <div className="flex flex-col gap-2.5">
                <div className="rounded-xl overflow-hidden" style={{ height: 160 }}>
                  <img src={imageUrl} alt="" className="w-full h-full object-cover block" />
                </div>
                <div className="flex gap-4 items-center">
                  <ImageUpload context="event-image" onUpload={setImageUrl}>
                    <span className="text-[13px] text-primary font-medium cursor-pointer font-sans">Change photo</span>
                  </ImageUpload>
                  <button type="button" onClick={() => setImageUrl(null)} className="text-[12px] text-muted bg-transparent border-0 cursor-pointer p-0 font-sans">
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <ImageUpload context="event-image" onUpload={setImageUrl}>
                <div className="h-[108px] border-[1.5px] border-dashed border-line-soft rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer">
                  <div className="text-[22px] mb-0.5">📷</div>
                  <div className="text-[13px] font-semibold text-ink-2">Add a photo</div>
                  <div className="text-[11.5px] text-muted">JPEG, PNG, or WebP · up to 5 MB</div>
                </div>
              </ImageUpload>
            )}
          </Section>

          {/* Attributes */}
          <Section title="Attributes">
            <div className="grid grid-cols-2 gap-2">
              {ATTRIBUTE_OPTIONS.map(({ key, label }) => {
                const checked = Boolean(attributes[key])
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] cursor-pointer border-[1.5px] ${checked ? 'bg-primary-soft border-primary' : 'bg-s2 border-line-soft'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setAttributes((prev) => ({ ...prev, [key]: e.target.checked || undefined }))}
                      aria-label={label}
                      className="w-[15px] h-[15px] cursor-pointer shrink-0 accent-[var(--primary)]"
                    />
                    <span className="text-[13px] font-medium">{label}</span>
                  </label>
                )
              })}
            </div>
          </Section>

          {/* Capability-gated metadata */}

          <CapabilityToggleRow
            bringADish={Boolean(attributes.bringADish)}
            outdoorActivity={Boolean(attributes.outdoorActivity)}
            onToggleDish={() => setAttributes((p) => ({ ...p, bringADish: p.bringADish ? undefined : true }))}
            onToggleOutdoor={() => setAttributes((p) => ({ ...p, outdoorActivity: p.outdoorActivity ? undefined : true }))}
          />

          {attributes.bringADish && (
            <Section title="Dish goals">
              <DishGoalsFields
                mainGoal={mainGoal} setMainGoal={setMainGoal}
                saladGoal={saladGoal} setSaladGoal={setSaladGoal}
                sideGoal={sideGoal} setSideGoal={setSideGoal}
                dessertGoal={dessertGoal} setDessertGoal={setDessertGoal}
                drinkGoal={drinkGoal} setDrinkGoal={setDrinkGoal}
              />
            </Section>
          )}

          {attributes.outdoorActivity && (
            <Section title="Outdoor details">
              <OutdoorDetailsFields
                difficulty={outdoorDifficulty} setDifficulty={setOutdoorDifficulty}
                distance={outdoorDistance} setDistance={setOutdoorDistance}
                elevation={outdoorElevation} setElevation={setOutdoorElevation}
                meetupNote={outdoorMeetupNote} setMeetupNote={setOutdoorMeetupNote}
              />
            </Section>
          )}

          {eventType === 'study' && (
            <Section title="Study details">
              <div className="flex flex-col gap-3.5">
                <Field label="Topic" htmlFor="study-topic">
                  <input id="study-topic" type="text" value={studyTopic} onChange={(e) => setStudyTopic(e.target.value)} className="input" />
                </Field>
                <Field label="Scripture reference" htmlFor="study-scripture">
                  <input id="study-scripture" type="text" value={studyScripture} onChange={(e) => setStudyScripture(e.target.value)} placeholder="e.g. John 3:16" className="input" />
                </Field>
              </div>
            </Section>
          )}

          {/* Audience — church-hosted only */}
          {isChurchHosted && churchData?.data?.denomination && churchData.data.denomination !== 'other' && (
            <Section title="Audience">
              <div className="flex gap-2">
                {([
                  { value: 'everyone', label: 'Everyone', sub: 'Visible to all app users' },
                  { value: 'denomination', label: `${churchData.data.denomination.replace(/-/g, ' ')} only`, sub: 'Only users who share your denomination' },
                ] as const).map(({ value, label, sub }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAudience(value)}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 10, textAlign: 'left',
                      border: `${audience === value ? 2 : 1}px solid ${audience === value ? 'var(--primary)' : 'var(--line-soft)'}`,
                      background: audience === value ? 'var(--primary-soft)' : 'var(--surface)',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', textTransform: 'capitalize' }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>
                  </button>
                ))}
              </div>
            </Section>
          )}

          {/* RSVP settings */}
          <Section title="RSVP settings">
            <div className="flex flex-col gap-4">
              <label className="flex items-center justify-between cursor-pointer gap-4">
                <div>
                  <div className="text-[14px] font-medium">Require approval</div>
                  <div className="text-[12px] text-muted mt-0.5">
                    You'll review each RSVP before the full address is shared
                  </div>
                </div>
                <input
                  type="checkbox"
                  aria-label="Require RSVP approval"
                  checked={rsvpRequiresApproval}
                  onChange={(e) => {
                    setRsvpRequiresApproval(e.target.checked)
                    if (!e.target.checked) setHideAddressUntilApproved(false)
                  }}
                  className="w-[18px] h-[18px] cursor-pointer shrink-0"
                />
              </label>
              {rsvpRequiresApproval && (
                <label className="flex items-center justify-between cursor-pointer gap-4 pl-4">
                  <div>
                    <div className="text-[13px] font-medium text-ink-2">Hide address until approved</div>
                    <div className="text-[12px] text-muted mt-0.5">
                      Address only revealed after you approve each RSVP.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    aria-label="Hide address until approved"
                    checked={hideAddressUntilApproved}
                    onChange={(e) => setHideAddressUntilApproved(e.target.checked)}
                    className="w-[18px] h-[18px] cursor-pointer shrink-0"
                  />
                </label>
              )}
              <Field label="Max attendees" htmlFor="max-attendees" hint="Leave blank for unlimited">
                <input
                  id="max-attendees"
                  type="number"
                  min="1"
                  value={maxAttendees}
                  onChange={(e) => setMaxAttendees(e.target.value)}
                  placeholder="Unlimited"
                  className="input"
                />
              </Field>
            </div>
          </Section>


          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn--lg flex-1"
            >
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </button>
            <Link to={`/events/${id}`} className="no-underline">
              <button type="button" className="btn btn--lg btn--ghost">
                Cancel
              </button>
            </Link>
          </div>

          {/* Delete event */}
          <div className="mt-8 pt-6 border-t border-line-soft">
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-[13px] text-danger-DEFAULT font-medium cursor-pointer bg-transparent border-none p-0 font-sans"
              >
                Delete event…
              </button>
            ) : (
              <div className="p-4 rounded-xl border border-danger-line bg-danger-soft">
                <div className="text-[13.5px] font-semibold text-danger-deep mb-1">Delete this event?</div>
                <div className="text-[12.5px] text-danger-deep mb-3 opacity-80">
                  This removes the event and all its occurrences permanently. RSVPs will be cancelled.
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!id) return
                      deleteEvent.mutate(id, {
                        onSuccess: () => navigate(isChurchHosted && event
                          ? `/churches/${churchData?.data?.slug ?? ''}/dashboard`
                          : '/'),
                      })
                    }}
                    disabled={deleteEvent.isPending}
                    className="btn btn--sm"
                    style={{ background: '#ef4444', color: '#fff', border: 'none' }}
                  >
                    {deleteEvent.isPending ? 'Deleting…' : 'Yes, delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="btn btn--sm btn--ghost"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

        </form>
      </div>
    </PublicLayout>
  )
}
